import { type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import Loader from 'components/Loader/Loader';
import Title from 'components/Title/Title';
import { AppRoutes } from 'constants/AppRoutes';
import { USERS_ROLE } from 'constants/interfaces';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import styles from './scss/TargetedMarketing.module.scss';

interface ITargetedMarketingGate {
  children: ReactNode,
}

export function TargetedMarketingGate({ children }: ITargetedMarketingGate): ReactElement {
  const { data: me, isFetching } = useUserMeQuery();
  if (isFetching) {
    return <Loader isPageLoading />;
  }
  if (me?.data.role !== USERS_ROLE.admin) {
    return <Navigate to={AppRoutes.launchpad} replace />;
  }
  return <div className={styles.gate}>{children}</div>;
}

interface ITargetedMarketingHeader {
  title: string,
  backTo: string,
  backLabel: string,
  actions: ReactNode | null,
}

export function TargetedMarketingHeader({
  title,
  backTo,
  backLabel,
  actions,
}: ITargetedMarketingHeader): ReactElement {
  const navigate = useNavigate();
  return (
    <div className={styles.header}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate(backTo)}
      >
        <ArrowLeftIcon className={styles.backButtonIcon} />
        <span>{backLabel}</span>
      </button>
      <div className={styles.pageHeading}>
        <Title className={styles.pageTitle} text={title} />
        {actions}
      </div>
    </div>
  );
}

type WatchTowerPrimaryView = 'companies' | 'topics' | 'runs';
type WatchTowerRunsSubView = 'runs' | 'scheduler';

interface IWatchTowerShell {
  actions: ReactNode | null,
  children: ReactNode,
  primary: WatchTowerPrimaryView,
  runsSubView: WatchTowerRunsSubView | null,
}

export function WatchTowerShell({
  actions,
  children,
  primary,
  runsSubView,
}: IWatchTowerShell): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companiesTabClass = primary === 'companies'
    ? `${styles.viewTab} ${styles.viewTabActive}`
    : styles.viewTab;
  const topicsTabClass = primary === 'topics'
    ? `${styles.viewTab} ${styles.viewTabActive}`
    : styles.viewTab;
  const runsTabClass = primary === 'runs'
    ? `${styles.viewTab} ${styles.viewTabActive}`
    : styles.viewTab;
  const runsSubClass = runsSubView === 'runs'
    ? `${styles.tabButton} ${styles.tabButtonActive}`
    : styles.tabButton;
  const schedulerSubClass = runsSubView === 'scheduler'
    ? `${styles.tabButton} ${styles.tabButtonActive}`
    : styles.tabButton;

  return (
    <TargetedMarketingGate>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <div className={styles.shellChrome}>
            <TargetedMarketingHeader
              title={t('targetedMarketing.watchTowerTitle')}
              backTo={AppRoutes.agentsManagement}
              backLabel={t('common.back')}
              actions={actions}
            />
            <nav
              className={styles.viewTabs}
              aria-label={t('targetedMarketing.watchTowerTitle')}
            >
              <button
                type="button"
                className={companiesTabClass}
                aria-current={primary === 'companies' ? 'page' : undefined}
                onClick={() => navigate(AppRoutes.watchTower)}
              >
                {t('targetedMarketing.companiesTab')}
              </button>
              <button
                type="button"
                className={topicsTabClass}
                aria-current={primary === 'topics' ? 'page' : undefined}
                onClick={() => navigate(AppRoutes.watchTowerTopics)}
              >
                {t('targetedMarketing.topicsTab')}
              </button>
              <button
                type="button"
                className={runsTabClass}
                aria-current={primary === 'runs' ? 'page' : undefined}
                onClick={() => navigate(AppRoutes.watchTowerRuns)}
              >
                {t('targetedMarketing.runsTitle')}
              </button>
            </nav>
            {primary === 'runs' ? (
              <nav
                className={styles.tabList}
                aria-label={t('targetedMarketing.runsTitle')}
              >
                <button
                  type="button"
                  className={runsSubClass}
                  aria-current={runsSubView === 'runs' ? 'page' : undefined}
                  onClick={() => navigate(AppRoutes.watchTowerRuns)}
                >
                  {t('targetedMarketing.runsTitle')}
                </button>
                <button
                  type="button"
                  className={schedulerSubClass}
                  aria-current={runsSubView === 'scheduler' ? 'page' : undefined}
                  onClick={() => navigate(AppRoutes.watchTowerSchedules)}
                >
                  {t('targetedMarketing.schedulerTab')}
                </button>
              </nav>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </TargetedMarketingGate>
  );
}
